import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedOverlayPositionChange, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  DOCUMENT,
} from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { NimbusCdkArrowCssClass, NimbusTooltipService } from '@nimbus-library/features/shared/services/tooltip/nimbusTooltip.service';

@Component({
  standalone: false,
  selector: 'nimbus-mat-icon-overlay',
  templateUrl: './mat-icon-overlay.component.html',
  styleUrls: ['./mat-icon-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatIconOverlayComponent implements OnInit, OnDestroy {
  @Input()
  public icon: { icon: string; weight: IconWeight };
  @Input()
  public name: string;
  @Input()
  public isMediumSize: boolean = false;
  @Input()
  public panelClass: string;
  @Input()
  public position: NimbusCdkArrowCssClass;
  @Input()
  public textTemplate: TemplateRef<unknown>;
  @Input()
  public showDelay: number = 400;

  @ViewChild(CdkConnectedOverlay, { static: true })
  public connectedOverlay: CdkConnectedOverlay;

  private readonly hoverThreshold = 16;

  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private mouseEventSubscription$: Subscription;

  public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public cdkArrowCssClass$: BehaviorSubject<string> = new BehaviorSubject(this.nimbusTooltipService.getCdkArrowCssClass());
  public cdkConnectedPositions: ConnectedPosition[];

  @HostBinding('class.has-text')
  public get hasTextDisplayed(): boolean {
    return !!this.textTemplate;
  }

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly nimbusTooltipService: NimbusTooltipService,
  ) {}

  private static isMouseInElement(elem: HTMLElement, clientX: number, clientY: number): boolean {
    const rect = elem.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.left + rect.width && clientY >= rect.top && clientY <= rect.top + rect.height;
  }

  public ngOnInit(): void {
    this.cdkConnectedPositions = this.nimbusTooltipService.getCdkConnectedPositions(this.position ? [this.position] : null);
  }

  public onPositionChange(e: ConnectedOverlayPositionChange): void {
    this.cdkArrowCssClass$.next(this.nimbusTooltipService.getCdkArrowCssClass(e));
  }

  public showOverlay(): void {
    if (this.isOpen$.getValue()) {
      return;
    }
    this.showTimeout = setTimeout(() => {
      this.showTimeout = null;
      if (!this.isOpen$.getValue()) {
        this.isOpen$.next(true);
      }
    }, this.showDelay);
  }

  public cancelOverlay(): void {
    if (this.showTimeout !== null) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  public ngOnDestroy(): void {
    this.cancelOverlay();
    this.mouseEventSubscription$?.unsubscribe();
  }

  public overlayOpened(): void {
    const hostEl: HTMLElement = (this.connectedOverlay.origin as CdkOverlayOrigin).elementRef.nativeElement;
    const overlayEl: HTMLElement = this.connectedOverlay.overlayRef.overlayElement;
    this.mouseEventSubscription$ = fromEvent(this.document, 'mouseover').subscribe((ev: MouseEvent) => {
      if (
        !(
          MatIconOverlayComponent.isMouseInElement(hostEl, ev.clientX, ev.clientY) ||
          MatIconOverlayComponent.isMouseInElement(overlayEl, ev.clientX + this.hoverThreshold, ev.clientY) ||
          MatIconOverlayComponent.isMouseInElement(overlayEl, ev.clientX - this.hoverThreshold, ev.clientY) ||
          MatIconOverlayComponent.isMouseInElement(overlayEl, ev.clientX, ev.clientY + this.hoverThreshold) ||
          MatIconOverlayComponent.isMouseInElement(overlayEl, ev.clientX, ev.clientY - this.hoverThreshold)
        )
      ) {
        this.isOpen$.next(false);
        ev.stopPropagation();
      }
    });
  }

  public overlayClosed(): void {
    this.mouseEventSubscription$ && this.mouseEventSubscription$.unsubscribe();
  }
}
